import { useRef, useState, type ReactNode } from 'react'
import { Trash2, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/errors'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'

/**
 * SingleDocumentUpload — единый блок загрузки ОДНОГО документа (centrika-docs, common.md
 * «Загрузка одного документа»). Дословно реализует все состояния раздела: «Документ не загружен»,
 * загрузка, документ загружен, замена, ошибка загрузки, удаление, ошибка удаления.
 *
 * Компонент УПРАВЛЯЕМЫЙ: текущее состояние документа задаётся пропом `file` (вызывающая сторона
 * после успешной загрузки/замены/удаления обновляет своё состояние — проп `file` меняется).
 * Внутри компонент держит только транзиентный статус (идёт загрузка/замена/удаление) и текст
 * ошибки. Колбэки `onUpload`/`onDelete` возвращают промис; их отклонение превращается в текст
 * ошибки по правилам раздела (известная причина — конкретный текст сервера/проверки размера,
 * неизвестная — «Не удалось загрузить документ» / «Не удалось удалить документ»).
 *
 * Обязательность, форматы (`accept`), максимальный размер (`maxSizeBytes`) и роли (`disabled` —
 * действия недоступны, скачивание существующего документа остаётся доступным; отсутствие
 * `onDelete` убирает удаление) определяются требованиями конкретной страницы/компонента.
 *
 * В компонент НЕ входят множественная загрузка и drag-and-drop. Кнопки — `type="button"`: блок
 * можно ставить внутрь формы, он её не отправляет.
 */

/** Текущий документ блока: название файла + ссылка на просмотр/скачивание. */
export interface SingleDocumentFile {
  name: string
  downloadUrl: string
}

export interface SingleDocumentUploadProps {
  /** Текущий документ; отсутствует/`null` — состояние «Документ не загружен». */
  file?: SingleDocumentFile | null
  /**
   * Загрузка выбранного файла (первичная или замена). Отклонение промиса → текст ошибки.
   * Вызывающая сторона по успеху обновляет проп `file`.
   */
  onUpload: (file: File) => Promise<void>
  /**
   * Удаление текущего документа (без подтверждения). Не передан — кнопка удаления не
   * показывается (страница не разрешает удаление). Отклонение промиса → «Не удалось удалить
   * документ», документ остаётся загруженным.
   */
  onDelete?: () => Promise<void>
  /** Допустимые форматы для системного окна выбора файла (атрибут `accept`). */
  accept?: string
  /**
   * Максимальный размер файла в байтах. При превышении загрузка не стартует, показывается
   * текст «Размер файла не должен превышать N МБ» (N — из этого значения).
   */
  maxSizeBytes?: number
  /**
   * Роли: действия (загрузка/замена/удаление) недоступны. Скачивание существующего документа
   * остаётся доступным.
   */
  disabled?: boolean
  /** Доступное имя (`aria-label`) скрытого поля выбора файла — для различения нескольких блоков. */
  inputAriaLabel?: string
  /**
   * Проброс контракта `Field` (render-prop): `id` связывает `<label htmlFor>` со скрытым полем
   * выбора файла (клик по метке открывает выбор), `aria-required`/`aria-invalid`/`aria-describedby`
   * несут состояние обязательности/ошибки для вспомогательных технологий (визуальная «*» у Field —
   * `aria-hidden`, поэтому сигнал обязан быть на самом контроле).
   */
  id?: string
  ariaRequired?: boolean
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  className?: string
  /**
   * Компактно — в одну строку для таблиц (ТЗ 26.10.2026, п.6.3: строки слушателей ниже): вместо
   * плашки «Документ не загружен» и кнопок с подписью — кнопки-значки, подпись в подсказке
   * (`title`) и в доступном имени. Тексты состояний и ошибок — те же.
   */
  compact?: boolean
  /** Что за документ — для подсказок компактных кнопок («Загрузить: Согласие ПД»). */
  documentLabel?: string
}

const MB = 1024 * 1024

type Status = 'idle' | 'uploading' | 'deleting'
interface UploadError {
  kind: 'upload' | 'delete'
  message: string
}

export function SingleDocumentUpload({
  file,
  onUpload,
  onDelete,
  accept,
  maxSizeBytes,
  disabled = false,
  inputAriaLabel,
  id,
  ariaRequired,
  ariaInvalid,
  ariaDescribedBy,
  className,
  compact = false,
  documentLabel,
}: SingleDocumentUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<UploadError | null>(null)

  const busy = status !== 'idle'
  const hasFile = Boolean(file)

  function openPicker() {
    inputRef.current?.click()
  }

  async function runUpload(selected: File) {
    // Известная причина — превышение размера: загрузка не стартует, конкретный текст.
    if (maxSizeBytes && selected.size > maxSizeBytes) {
      const limitMb = Math.round(maxSizeBytes / MB)
      setError({ kind: 'upload', message: `Размер файла не должен превышать ${limitMb} МБ` })
      return
    }
    setError(null)
    setStatus('uploading')
    try {
      await onUpload(selected)
    } catch (err) {
      setError({ kind: 'upload', message: getErrorMessage(err, 'Не удалось загрузить документ') })
    } finally {
      setStatus('idle')
    }
  }

  async function runDelete() {
    if (!onDelete) return
    setError(null)
    setStatus('deleting')
    try {
      await onDelete()
    } catch (err) {
      setError({ kind: 'delete', message: getErrorMessage(err, 'Не удалось удалить документ') })
    } finally {
      setStatus('idle')
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    // Сброс значения — чтобы повторный выбор того же файла снова вызвал onChange.
    e.target.value = ''
    if (selected) void runUpload(selected)
  }

  const errorText = error ? <span className="text-12 text-red">{error.message}</span> : null
  const hint = (action: string) => (documentLabel ? `${action}: ${documentLabel}` : action)

  let body: ReactNode
  if (compact) {
    const iconButton = (action: string, icon: ReactNode, onClick: () => void) => (
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="px-1.5 py-1"
        aria-label={hint(action)}
        title={hint(action)}
        onClick={onClick}
      >
        {icon}
      </Button>
    )
    body = (
      <>
        <div className="flex min-w-0 items-center gap-1.5">
          {hasFile && file ? (
            <a
              className="min-w-0 break-words text-12 text-accent underline"
              href={file.downloadUrl}
              download
            >
              {file.name}
            </a>
          ) : status === 'uploading' ? null : (
            <span className="whitespace-nowrap text-12 text-txt-tertiary">Не загружен</span>
          )}
          {disabled ? null : status === 'deleting' ? (
            <span className="text-12 text-txt-secondary">Удаление…</span>
          ) : status === 'uploading' ? (
            <span className="text-12 text-txt-secondary">Загрузка…</span>
          ) : (
            <>
              {iconButton(
                hasFile ? 'Заменить' : 'Загрузить',
                <Upload className="size-3.5 stroke-current" aria-hidden />,
                openPicker,
              )}
              {hasFile && onDelete
                ? iconButton('Удалить документ', <Trash2 className="size-3.5 stroke-current" aria-hidden />, () =>
                    void runDelete(),
                  )
                : null}
            </>
          )}
        </div>
        {errorText}
      </>
    )
  } else if (hasFile && file) {
    // === Документ загружен / замена / удаление / ошибки при существующем документе ===
    body = (
      <>
        {/* Длинное название переносится и не выходит за границы блока. */}
        <div className="min-w-0">
          <a
            className="break-words text-12 text-accent underline"
            href={file.downloadUrl}
            download
          >
            {file.name}
          </a>
        </div>
        {disabled ? null : status === 'deleting' ? (
          <span className="text-12 text-txt-secondary">Удаление…</span>
        ) : status === 'uploading' ? (
          // Во время замены имя видно, действия недоступны, рядом «Загрузка…».
          <span className="text-12 text-txt-secondary">Загрузка…</span>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={openPicker}
              leftIcon={<Upload className="size-3.5 stroke-current" aria-hidden />}
            >
              Заменить
            </Button>
            {onDelete ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                aria-label="Удалить документ"
                title="Удалить документ"
                onClick={() => void runDelete()}
              >
                <Trash2 className="size-3.5 stroke-current" aria-hidden />
              </Button>
            ) : null}
          </div>
        )}
        {errorText}
      </>
    )
  } else if (status === 'uploading') {
    // === Первоначальная загрузка ===
    body = <span className="text-12 text-txt-secondary">Загрузка…</span>
  } else {
    // === Документ не загружен / ошибка первичной загрузки ===
    body = (
      <>
        {error?.kind === 'upload' ? (
          errorText
        ) : (
          <Badge variant="gray">Документ не загружен</Badge>
        )}
        {disabled ? null : (
          <div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={openPicker}
              leftIcon={<Upload className="size-3.5 stroke-current" aria-hidden />}
            >
              Загрузить
            </Button>
          </div>
        )}
      </>
    )
  }

  return (
    <div className={cn('flex flex-col items-start gap-1.5', className)}>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        aria-label={inputAriaLabel}
        aria-required={ariaRequired}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className="hidden"
        disabled={disabled || busy}
        onChange={handleChange}
      />
      {body}
    </div>
  )
}
